import React, { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabaseClient';
import { useSubcontractorProfile } from '@/contexts/SubcontractorProfileContext';
import { useUnreadMessages } from '@/contexts/UnreadMessagesContext';
import { Button } from '@/components/ui/button';
import { BellRing, Loader2 } from 'lucide-react';

// Messages in these categories hard-lock the driver app until acknowledged.
const BLOCKING_CATEGORIES = ['daily_protocol', 'ai_coaching', 'incident_coaching'];

const publicUrl = (path) => {
  try { return supabase.storage.from('message-attachments').getPublicUrl(path).data.publicUrl; }
  catch { return null; }
};

// Renders children always (so in-progress input is preserved), and overlays a blocking
// modal whenever there's an unread protocol/coaching message. Real-time: a new message
// interrupts immediately. Acknowledge = one tap, then access returns.
const MessageGate = ({ children }) => {
  const { profile } = useSubcontractorProfile();
  const { markMessageAsRead } = useUnreadMessages();
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ack, setAck] = useState(false);
  const channelRef = useRef(null);

  const check = useCallback(async () => {
    if (!profile?.id) { setPending([]); setLoading(false); return; }
    try {
      const { data } = await supabase
        .from('message_recipients')
        .select('messages!inner(id, title, body, attachments, created_at)')
        .eq('subcontractor_id', profile.id)
        .is('read_at', null);
      const blocking = (data || [])
        .map((r) => r.messages)
        .filter((m) => m?.attachments && BLOCKING_CATEGORIES.includes(m.attachments.category))
        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at)); // oldest first
      setPending(blocking);
    } finally {
      setLoading(false);
    }
  }, [profile?.id]);

  useEffect(() => { check(); }, [check]);

  // Real-time interrupt: a newly delivered blocking message takes over immediately.
  useEffect(() => {
    if (!profile?.id) return;
    const ch = supabase
      .channel(`msg-gate-${profile.id}`)
      .on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'message_recipients', filter: `subcontractor_id=eq.${profile.id}` },
        () => check())
      .subscribe();
    channelRef.current = ch;
    return () => { supabase.removeChannel(ch).catch(() => {}); };
  }, [profile?.id, check]);

  const current = pending[0];

  const acknowledge = async () => {
    if (!current || !profile?.id) return;
    setAck(true);
    try {
      // Persist read state on the recipient row the gate actually reads from,
      // keyed to THIS subcontractor — so a confirmed protocol never re-appears,
      // independent of the unread-count context being populated.
      const { error } = await supabase
        .from('message_recipients')
        .update({ read_at: new Date().toISOString(), delivery_status: 'read' })
        .eq('message_id', current.id)
        .eq('subcontractor_id', profile.id)
        .is('read_at', null);
      if (error) throw error;
      // Keep the unread badge / read_status in sync (best-effort, non-blocking).
      markMessageAsRead(current.id).catch(() => {});
      setPending((prev) => prev.filter((m) => m.id !== current.id));
    } catch {
      /* keep blocking on failure */
    } finally {
      setAck(false);
    }
  };

  const files = Array.isArray(current?.attachments?.files) ? current.attachments.files : [];
  const isCoaching = current?.attachments?.category === 'ai_coaching';

  return (
    <>
      {children}
      {!loading && current && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/85 backdrop-blur-sm p-4">
          <div className="w-full max-w-md rounded-2xl bg-white dark:bg-slate-900 shadow-2xl border border-slate-200 dark:border-slate-700 p-6 text-center">
            <div className="relative mx-auto mb-4 w-16 h-16">
              <span className="absolute inset-0 rounded-full bg-emerald-400/50 animate-ping" />
              <span className="relative flex items-center justify-center w-16 h-16 rounded-full bg-emerald-500 text-white shadow-lg">
                <BellRing className="h-7 w-7" />
              </span>
            </div>
            <p className="text-[11px] font-bold uppercase tracking-widest text-emerald-600">{isCoaching ? 'Performance coaching' : 'Action required'}</p>
            <h2 className="mt-1 text-lg font-bold text-slate-900 dark:text-white">{current.title}</h2>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-300 whitespace-pre-wrap">{current.body}</p>

            {files.length > 0 && (
              <div className="mt-4 space-y-2">
                {files.map((f, i) => {
                  const url = publicUrl(f.storage_path);
                  return url ? (
                    <img key={i} src={url} alt={f.file_name || 'attachment'} className="w-full rounded-lg border border-slate-200 dark:border-slate-700" />
                  ) : null;
                })}
              </div>
            )}

            {pending.length > 1 && (
              <p className="mt-3 text-xs text-slate-400">{pending.length - 1} more message{pending.length - 1 === 1 ? '' : 's'} to read after this</p>
            )}

            <Button onClick={acknowledge} disabled={ack} className="mt-5 w-full bg-emerald-600 hover:bg-emerald-700 text-white">
              {ack ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <BellRing className="h-4 w-4 mr-2" />}
              I've read this
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default MessageGate;
